import { ListItemButton, ListItemButtonProps } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import React from 'react'
import { withRouter } from 'react-router-dom'
import RestrictedView from '../auth/RestrictedView'
import { i18n } from '../index'
import type { Station } from '../station/stationTypes'
import withActiveStation from '../station/withActiveStation'
import css from './navigation.css'

type Props = {
  open?: boolean
  onClose: (...args: Array<any>) => any
  onOpen: (...args: Array<any>) => any
  activeStation?: Station
  testId?: string
}

type SubHeaderProps = {
  text: string
  onClick: (...args: Array<any>) => any
}

const Subheader = ({ text, onClick }: SubHeaderProps) => {
  return (
    <ListItem component='div' className={css.subheader} onClick={onClick}>
      {text}
    </ListItem>
  )
}

const NavItem = withRouter(({ text, link, history }: any) => {
  const itemProps: ListItemButtonProps = {}

  if (link) {
    itemProps.onClick = () => {
      history.push({
        pathname: link,
      })
    }
  }

  return (
    <ListItemButton {...itemProps}>
      <ListItemText primary={text} className={css.listItem} />
    </ListItemButton>
  )
})

type State = {
  open: {
    infos: boolean
    cooking: boolean
    admin: boolean
    report: boolean
    expressCheckout: boolean
  }
}

@withActiveStation
class Navigation extends React.Component<Props, State> {
  state: State = {
    open: {
      infos: false,
      cooking: false,
      admin: false,
      report: false,
      expressCheckout: false,
    },
  }

  toggleOpen = (e: any, key: string) => {
    this.setState({
      open: {
        ...this.state.open,
        [key]: !this.state.open[key],
      },
    })

    e.preventDefault()
    e.stopPropagation()
  }

  isOpen = (key: string) => {
    return !!this.state.open[key]
  }

  render() {
    const { open, onClose, onOpen, activeStation } = this.props
    const activeStationId = activeStation ? activeStation.id : 'undefined'
    const activeStationIdUrl = encodeURIComponent(activeStationId)

    return (
      <div className={css.component}>
        <SwipeableDrawer open={open} onClose={onClose} onOpen={onOpen}>
          <div className={css.navContainer} tabIndex={0} role='button' onKeyDown={onClose}>
            <RestrictedView requiredPermission='nav_basic'>
              <List component='nav' className={css.list}>
                <NavItem text={i18n.t('navigation.item.home')} link='/' />
                <NavItem text={i18n.t('navigation.item.employeeCard')} link='/tyontekijakortti' />
                <NavItem text={i18n.t('navigation.item.dailyPlanning')} link='/vuorokausijohtaminen' />
                <NavItem text={i18n.t('navigation.item.budgetPlanning')} link='/myynnit-ja-tunnit' />
                <NavItem text={i18n.t('navigation.item.taskList')} link='/tehtavalista' />
                <NavItem text={i18n.t('navigation.item.guidelinesAndLinks')} link='/ohjeet-ja-linkit' />
                <NavItem text={i18n.t('navigation.item.introduction')} link='/perehdytys' />
                <NavItem
                  text={i18n.t('navigation.item.scorecard')}
                  link={`/tuloskortti/${activeStation ? activeStation.id : ''}`}
                />
              </List>
            </RestrictedView>
            <RestrictedView requiredPermission='nav_infos'>
              <List
                component='nav'
                subheader={
                  <Subheader text={i18n.t('navigation.item.infos')} onClick={(e) => this.toggleOpen(e, 'infos')} />
                }
              >
                <Collapse in={this.isOpen('infos')} timeout='auto' unmountOnExit>
                  <NavItem text={i18n.t('navigation.item.weeklyInfo')} link='/viikkoinfo' />
                  <NavItem text={i18n.t('navigation.item.monthlyInfo')} link='/kuukausi-info' />
                  <NavItem text={i18n.t('navigation.item.dailyInfo')} link='/ravintolainfo' />
                  <NavItem text={i18n.t('navigation.item.campaigns')} link='/kampanjat' />
                </Collapse>
              </List>
            </RestrictedView>
            <RestrictedView requiredPermission='nav_cooking'>
              <List
                component='nav'
                subheader={
                  <Subheader text={i18n.t('navigation.item.cooking')} onClick={(e) => this.toggleOpen(e, 'cooking')} />
                }
              >
                <Collapse in={this.isOpen('cooking')} timeout='auto' unmountOnExit>
                  <NavItem
                    text={i18n.t('navigation.item.customerTarget')}
                    link='/asiakasmaaratavoitteet-seka-toteumat'
                  />
                  <NavItem text={i18n.t('navigation.item.products')} link='/vitriinien-esillepano' />
                </Collapse>
              </List>
            </RestrictedView>
            <RestrictedView requiredPermission='nav_basic'>
              <List
                component='nav'
                subheader={
                  <Subheader text={i18n.t('navigation.item.reporting')} onClick={(e) => this.toggleOpen(e, 'report')} />
                }
              >
                <Collapse in={this.isOpen('report')} timeout='auto' unmountOnExit>
                  <RestrictedView requiredPermission='nav_manager'>
                    <NavItem
                      text={i18n.t('navigation.item.incentiveSales')}
                      link='/raportointi/aloitteellinen-myynti'
                    />
                  </RestrictedView>
                  <NavItem text={i18n.t('navigation.item.customerAmounts')} link='/raportointi/asiakasmaarat' />
                  <NavItem text={i18n.t('navigation.item.totalSales')} link='/raportointi/kokonaismyynti' />
                  <RestrictedView requiredPermission='nav_manager'>
                    <NavItem text={i18n.t('navigation.item.feelingsMeter')} link='/raportointi/fiilismittari' />
                  </RestrictedView>
                  <RestrictedView requiredPermission='nav_admin'>
                    <NavItem
                      text={i18n.t('navigation.item.feelingsMeterFeedbacks')}
                      link='/raportointi/fiilismittaripalautteet'
                    />
                  </RestrictedView>
                  <NavItem text={i18n.t('navigation.item.loss')} link='/raportointi/havikki' />
                  <RestrictedView requiredPermission='nav_manager'>
                    <NavItem text={i18n.t('navigation.item.thumbsUp')} link='/raportointi/peukutettavat' />
                  </RestrictedView>
                </Collapse>
              </List>
            </RestrictedView>
            <RestrictedView requiredPermission='nav_basic'>
              <List
                component='nav'
                subheader={
                  <Subheader
                    text={i18n.t('navigation.item.expressCheckout')}
                    onClick={(e) => this.toggleOpen(e, 'expressCheckout')}
                  />
                }
              >
                <Collapse in={this.isOpen('expressCheckout')} timeout='auto' unmountOnExit>
                  <NavItem
                    text={i18n.t('navigation.item.openingHours')}
                    link={`/pikakassa/${activeStationIdUrl}/aukioloajat`}
                  />
                  <NavItem
                    text={i18n.t('navigation.item.productAvailabilities')}
                    link={`/pikakassa/${activeStationIdUrl}/tuotesaatavuudet`}
                  />
                </Collapse>
              </List>
            </RestrictedView>
            <RestrictedView requiredPermission='nav_manager'>
              <List
                component='nav'
                subheader={
                  <Subheader
                    text={i18n.t('navigation.item.admin.title')}
                    onClick={(e) => this.toggleOpen(e, 'admin')}
                  />
                }
              >
                <Collapse in={this.isOpen('admin')} timeout='auto' unmountOnExit>
                  {activeStation && (
                    <NavItem
                      text={i18n.t('navigation.item.admin.ownStation')}
                      link={`/yllapito/toimipaikat/perustiedot/${activeStation.id}`}
                      manageOwnStation={true}
                    />
                  )}
                  <RestrictedView requiredPermission='nav_admin'>
                    <NavItem text={i18n.t('navigation.item.admin.stations')} link='/yllapito/toimipaikat' />
                    <RestrictedView requiredPermission='manage_chain'>
                      <NavItem text={i18n.t('navigation.item.admin.chain')} link='/yllapito/ketju' />
                    </RestrictedView>
                  </RestrictedView>
                  <NavItem text={i18n.t('navigation.item.admin.users')} link='/yllapito/kayttajat' />
                  <NavItem text={i18n.t('navigation.item.admin.bakeryProducts')} link='/yllapito/vitriinituotteet' />
                  <RestrictedView requiredPermission='nav_admin'>
                    <NavItem
                      text={i18n.t('navigation.item.admin.guidelinesAndLinks')}
                      link='/yllapito/ohjeet-ja-linkit'
                    />
                  </RestrictedView>
                  <RestrictedView requiredPermission='nav_admin'>
                    <NavItem text={i18n.t('navigation.item.admin.introduction')} link='/yllapito/perehdytys' />
                  </RestrictedView>
                </Collapse>
              </List>
            </RestrictedView>
          </div>
        </SwipeableDrawer>
      </div>
    )
  }
}

export default Navigation
